<template>
  <view-wrapper
    prefix="Add new"
    name="Printer"
    path="printers"
    :showCancel="true"
    addedText="Add printer"
    :addedFunc="postPrinter"
    :disabledFlag="!isAddButtonEnabled"
    :disabledMessage="pclLimitReachedMessage"
    :hasChangesBeenMade="hasChangesBeenMade"
    :loading="loading"
  >
    <modal
      v-if="showWarning"
      @close="closeModal()"
      type="warning"
      heading="Warning"
      :description="warningText"
    >
      <template slot="body">
        <h3>Warning</h3>
        <p class="m-0">{{ warningText }}</p>
        <div v-if="activeWarningKey" class="mt-4 flex items-center justify-center">
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="dontShowAgain"
              class="mr-2 w-4 h-4 text-primary-light border-gray-300 rounded focus:ring-primary-light"
            />
            <span class="text-sm font-medium text-primary-dark">Don't show this again</span>
          </label>
        </div>
      </template>
    </modal>

    <wrapper class="flex justify-between mb-3">
      <drop-down
        class="w-96"
        placeholder="Select vendor"
        :disabled="subscriptionSettings === null"
        :value="selectedVendor"
        valueProperty="name"
        searchProp="name"
        :options="vendors"
        @input="
          (e) => {
            selectedVendor = e;
            selectedModel = {};
          }
        "
      />
      <drop-down
        class="w-96"
        placeholder="Select model"
        :value="selectedModel"
        valueProperty="name"
        searchProp="name"
        :options="printerModels"
        @input="selectedModel = $event"
      />
    </wrapper>

    <wrapper :checkForChildFocused="true" class="px-5 py-10 mb-3">
      <grafokett-input
        v-model="printerName"
        :editable="true"
        labelPlaceholder="Printer name"
      />
    </wrapper>

    <wrapper :checkForChildFocused="true" class="px-5 py-10 mb-3">
      <grafokett-input
        v-model="ip"
        :editable="true"
        labelPlaceholder="IP-adress"
      />
    </wrapper>

    <wrapper
      :checkForChildFocused="true"
      class="px-5 py-10 mb-20"
      :focus="port !== ''"
    >
      <grafokett-input
        v-model="port"
        :editable="true"
        labelPlaceholder="Port"
      />
    </wrapper>

    <assign-groups
      class="mt-16 mb-5"
      :groups="availableGroups"
      v-model="groups"
      :useCheckbox="false"
    />
  </view-wrapper>
</template>

<script>
import Modal from "../components/Modal";
import AssignGroups from "../components/datacomponents/AssignGroups";
import DropDown from "../components/forms/DropDown.vue";
import GrafokettInput from "../components/forms/Input.vue";
import { getPreference, setPreference } from "@/utils/userPreferences";

export default {
  components: {
    Modal,
    AssignGroups,
    GrafokettInput,
    DropDown,
  },
  data() {
    return {
      selectedVendor: {},
      printerModels: [],
      selectedModel: {},
      printerName: "",
      ip: "",
      port: "9100",
      groups: [],
      headersUsers: {
        name: "Name",
        vendor: "Vendor",
        model: "Model",
      },
      loading: true,
      showWarning: false,
      dontShowAgain: false,
      activeWarningKey: null,
    };
  },
  computed: {
    isAddButtonEnabled() {
      return (
        this.printerName &&
        this.ip &&
        this.groups.length > 0 &&
        !(this.isSelectedModelPclPrinter && this.maxPclPrintersReached)
      );
    },
    isFormComplete() {
      return this.printerName && this.ip && this.groups.length > 0;
    },
    subscriptionSettings() {
      return this.$store.getters["SUBSCRIPTION_SETTINGS/SUBSCRIPTION_SETTINGS"];
    },
    isSelectedModelPclPrinter() {
      const vendor = (this.selectedVendor.name || "").toLowerCase();
      const model = (this.selectedModel.name || "").toLowerCase();
      return (
        vendor === "microsoft" && model === "microsoft pcl6 class driver"
      );
    },
    maxPclPrintersReached() {
      if (
        !this.subscriptionSettings ||
        !this.subscriptionSettings.maxPCLPrinters
      )
        return false;
      return (
        this.subscriptionSettings.pclPrinters >=
        this.subscriptionSettings.maxPCLPrinters
      );
    },
    pclLimitReachedMessage() {
      if (!this.isSelectedModelPclPrinter || !this.maxPclPrintersReached)
        return "";
      return `Max limit of PCL printers reached (${this.subscriptionSettings.maxPCLPrinters})`;
    },
    installedPrinters() {
      return this.$store.getters["PRINTERS/INSTALLED_PRINTERS"].map((p) => {
        return {
          id: p.id,
          name: p.name,
          ip: p.ipAddress,
          port: p.port,
          model: p.model,
          vendor: p.vendor,
          expandableData: {},
        };
      });
    },
    uniquePrinterModels() {
      // Använd korrekt värde från subscriptionSettings som räknar alla printers i organisationen
      if (this.subscriptionSettings && this.subscriptionSettings.printerModels !== undefined) {
        return this.subscriptionSettings.printerModels;
      }
      // Fallback: räkna från installedPrinters om subscriptionSettings inte är tillgänglig
      const installedPrinters =
        this.$store.getters["PRINTERS/INSTALLED_PRINTERS"];
      const uniqueModels = installedPrinters
        .map((p) => ({ model: p.model, id: p.id }))
        .filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.model === value.model)
        );
      return uniqueModels.length;
    },
    vendors() {
      let maxPrintersReached = true;
      if (this.subscriptionSettings !== null)
        maxPrintersReached =
          this.uniquePrinterModels >=
          this.subscriptionSettings.maxPrinterModels;
      if (maxPrintersReached) {
        const list = [];
        for (let i = 0; i < this.installedPrinters.length; i++) {
          if (
            !list.some((value) => {
              return value.name === this.installedPrinters[i].vendor;
            })
          ) {
            list.push({
              name: this.installedPrinters[i].vendor,
            });
          }
        }
        return list;
      }
      return this.$store.getters["PRINTERS/VENDORS"].map((v) => {
        return {
          name: v,
        };
      });
    },
    availableGroups() {
      return this.$store.getters["GROUPS/GROUPS"];
    },
  },
  watch: {
    selectedVendor() {
      if (this.selectedVendor.name) {
        this.getPrinterModels();
      }
    },
    isAddButtonEnabled(newValue) {
      this.disabledFlag = !newValue;
    },
    selectedModel() {
      const hidePclWarning = getPreference(
        "warnings",
        "hidePclPrinterLimitWarning",
        false
      );
      if (
        !hidePclWarning &&
        this.isSelectedModelPclPrinter &&
        this.maxPclPrintersReached
      ) {
        this.warningText = `Limit of PCL printers reached! (${this.subscriptionSettings.maxPCLPrinters})`;
        this.activeWarningKey = "hidePclPrinterLimitWarning";
        this.showWarning = true;
      }
    },
  },
  methods: {
    closeModal() {
      this.showWarning = false;
      if (this.dontShowAgain && this.activeWarningKey) {
        setPreference('warnings', this.activeWarningKey, true);
      }
    },
    hasChangesBeenMade() {
      return (
        this.selectedVendor.name !== undefined ||
        this.selectedModel.name !== undefined ||
        this.printerName !== "" ||
        this.ip !== "" ||
        this.groups.length > 0
      );
    },
    async getPrinterModels() {
      if (
        this.uniquePrinterModels >=
        this.subscriptionSettings.maxPrinterModels
      ) {
        const list = [];
        for (let i = 0; i < this.installedPrinters.length; i++) {
          const installedPrinter = this.installedPrinters[i];
          if (
            this.selectedVendor.name === installedPrinter.vendor &&
            !list.some((value) => {
              return value.model === installedPrinter.model;
            })
          ) {
            list.push({
              vendor: installedPrinter.vendor,
              model: installedPrinter.model,
            });
          }
        }
        this.printerModels = list.map((pm) => {
          return {
            name: pm.model,
          };
        });
      } else {
        const response = await this.$store.$api.get(
          `/api/printers/vendors/${this.selectedVendor.name}/models`
        );
        this.printerModels = response.data.data.map((pm) => {
          return {
            name: pm,
          };
        });
      }
    },
    async getData() {
      await this.$store.dispatch(
        "SUBSCRIPTION_SETTINGS/GET_SUBSCRIPTION_SETTINGS"
      );
      await this.$store.dispatch("PRINTERS/GET_INSTALLED_PRINTERS");
      await this.$store.dispatch("PRINTERS/GET_VENDORS");
      await this.$store.dispatch("GROUPS/GET_AVAILABLE_GROUPS");
    },
    async postPrinter() {
      this.loading = true;
      const payload = {
        name: this.printerName,
        vendor: this.selectedVendor.name,
        model: this.selectedModel.name,
        ipAddress: this.ip,
        port: this.port,
        printerGroups: this.groups.map((e) => {
          return {
            groupId: e.id,
            defaultForGroup: e.checked,
          };
        }),
      };
      try {
        await this.$store.dispatch("PRINTERS/ADD", payload);
        this.loading = false;
      } catch (error) {
        console.error(error);
        this.loading = false;
        const data = error.response && error.response.data;
        const message = data && (data.Message || data.message);
        if (message) {
          this.warningText = message;
          this.activeWarningKey = null;
          this.showWarning = true;
        }
      }
    },
  },
  async beforeMount() {
    await this.getData();
    this.loading = false;
    if (this.subscriptionSettings !== null) {
      this.warningText = `Limit of unique printer models reached! (${
        this.subscriptionSettings.maxPrinterModels
      })`;
      this.activeWarningKey = 'hidePrinterLimitWarning';

      // Check if user has chosen to hide this warning
      const hideWarning = getPreference('warnings', 'hidePrinterLimitWarning', false);

      this.showWarning =
        !hideWarning &&
        this.uniquePrinterModels >=
        this.subscriptionSettings.maxPrinterModels;
    }
  },
};
</script>

<style lang="postcss">
.printer-view {
  @apply m-8;
}
</style>
